import i18n from '../i18n';

// Dates arrive as UTC and are read in the operator's timezone, written in the
// language they chose in the app rather than the one their browser runs in.
export function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(i18n.language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
}

export function minutesSince(iso) {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
}

export function formatMinutes(minutes, t) {
  if (minutes < 60) return t('crm.age.minutes', { count: Math.max(minutes, 1) });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('crm.age.hours', { count: hours });
  return t('crm.age.days', { count: Math.floor(hours / 24) });
}

// An age, not a timestamp: the queue is read by how long someone has waited.
export function formatAge(iso, t) {
  const minutes = minutesSince(iso);
  return minutes < 1 ? t('crm.age.justNow') : formatMinutes(minutes, t);
}

// The API sends the ISO region the number belongs to; the browser already knows
// what that country is called in the reader's language.
export function countryName(region, lang) {
  if (!region) return '';
  try {
    return new Intl.DisplayNames([lang], { type: 'region' }).of(region) || region;
  } catch {
    // A non-geographic range (satellite numbers) has no country name - show the code.
    return region;
  }
}

// The number is never shown on screen, only its country and operator code and
// last two digits; the contact buttons still dial and copy the full number.
export function maskedPhone(phone) {
  const groups = phone.display.split(' ');
  const kept = groups.length > 2 ? 2 : 1;
  const rest = groups.slice(kept).join(' ');
  let hidden = rest.replace(/\D/g, '').length - 2;
  return [...groups.slice(0, kept), rest.replace(/\d/g, (digit) => (hidden-- > 0 ? '•' : digit))].join(' ');
}

export function phoneFacts(phone, lang) {
  if (!phone) return [];
  return [countryName(phone.region, lang), phone.carrier].filter(Boolean);
}

export function formatMoney(value, lang) {
  return new Intl.NumberFormat(lang, { maximumFractionDigits: 2 }).format(value);
}

// The crm.lead.* locale keys are the camelCase of the API field names.
export function apiFieldToKey(field) {
  return field.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

// Mirrors the backend's is_snoozed, which is what silences the reminders.
export function snoozedUntil(lead) {
  if (!lead.next_action_at) return null;
  return new Date(lead.next_action_at) > new Date() ? lead.next_action_at : null;
}

// A datetime-local input reads its value as LOCAL time, so an ISO string sliced
// to 16 characters shows a UTC moment three hours out of place in Kyiv.
export function toLocalInput(iso) {
  if (!iso) return '';
  const when = new Date(iso);
  const pad = (value) => String(value).padStart(2, '0');
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}` +
    `T${pad(when.getHours())}:${pad(when.getMinutes())}`
  );
}
